<template>
  <div class="invoice-page">
    <!-- 实名认证拦截弹窗 -->
    <div class="auth-modal" v-if="showAuthModal">
      <div class="auth-modal-content">
        <h3 class="auth-modal-title">实名认证提醒</h3>
        <p class="auth-modal-desc">
          您尚未完成实名认证，无法进行开票相关操作。<br>
          请先完善实名认证信息，完成后即可正常开具发票。<br>
          如有疑问，欢迎随时联系我们。
        </p>
        <button class="auth-modal-btn" @click="goToAuthPage">去完善</button>
      </div>
    </div>

    <!-- 筛选区域 -->
    <div class="filter-area" v-if="isAuthenticated">
      <div class="filter-item">
        <label class="filter-label">搜索:</label>
        <input type="text" class="filter-input" placeholder="订单号、买家姓名、邮箱" v-model="filterForm.keyword">
      </div>
      <div class="filter-item">
        <label class="filter-label">开票状态:</label>
        <select class="filter-select" v-model="filterForm.invoiceStatus">
          <option value="">全部状态</option>
          <option value="待开票">待开票</option>
          <option value="已完成">已完成</option>
        </select>
      </div>
      <div class="filter-item">
        <label class="filter-label">开票类型:</label>
        <select class="filter-select" v-model="filterForm.invoicePersonType">
          <option value="">全部类型</option>
          <option value="企业">企业</option>
          <option value="个人">个人</option>
        </select>
      </div>
      <div class="filter-buttons">
        <button class="filter-btn" @click="handleSearch">查询</button>
        <button class="filter-btn" @click="handleReset">重置</button>
      </div>
    </div>

    <!-- 开票列表 -->
    <div class="invoice-list" v-if="isAuthenticated">
      <table class="invoice-table">
        <thead>
          <tr>
            <th>订单号</th>
            <th>买家信息</th>
            <th>开票金额</th>
            <th>发票类型</th>
            <th>开票类型</th>
            <th>发票抬头</th>
            <th>申请时间</th>
            <th>开票状态</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <!-- 加载中提示 -->
          <tr v-if="loading">
            <td colspan="9" style="text-align: center; padding: 20px;">
              加载中...
            </td>
          </tr>
          <!-- 无数据提示 -->
          <tr v-else-if="total === 0">
            <td colspan="9" style="text-align: center; padding: 20px;">
              暂无相关开票数据
            </td>
          </tr>
          <!-- 数据列表 -->
          <tr v-else v-for="item in invoiceList" :key="item.id">
            <td>{{ item.orderNo }}</td>
            <td class="buyer-info">
              <img :src="item.userAvatar || 'https://via.placeholder.com/40x40?text=暂无'" class="buyer-avatar">
              <div class="buyer-detail">
                <div class="buyer-name">{{ item.userName }}</div>
                <div class="buyer-contact">{{ item.contact || '-' }}</div>
              </div>
            </td>
            <td class="price">¥{{ item.invoiceAmount }}</td>
            <td>
              <span class="tag info-tag">{{ item.invoiceType }}</span>
            </td>
            <td>
              <span class="tag success-tag" v-if="item.invoicePersonType === '企业'">企业</span>
              <span class="tag info-tag" v-else>个人</span>
            </td>
            <td class="title-text">{{ item.invoiceTitle }}</td>
            <td>{{ item.applyTime }}</td>
            <td>
              <span class="tag warning-tag" v-if="item.invoiceStatus === '待开票'">待开票</span>
              <span class="tag success-tag" v-else>已完成</span>
            </td>
            <td>
              <button class="operate-btn primary-btn" @click="openInvoiceDialog(item)"
                v-if="item.invoiceStatus === '待开票'">
                开票
              </button>
              <button class="operate-btn text-btn" @click="viewInvoiceRecord(item)" v-else>
                查看
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 分页组件 -->
    <div class="pagination-container" v-if="total > 0 && isAuthenticated">
      <el-pagination @current-change="handleCurrentChange" :current-page="pagination.pageNum"
        :page-size="pagination.pageSize" layout="total, prev, pager, next, jumper" :total="total" background>
      </el-pagination>
    </div>

    <!-- 未认证提示 -->
    <div class="unauth-tip" v-if="!isAuthenticated">
      <div class="tip-content">
        <i class="tip-icon"></i>
        <p>请先完成实名认证，才能进行开票相关操作</p>
        <button class="tip-btn" @click="goToAuthPage">去实名认证</button>
      </div>
    </div>

    <!-- 开票弹窗 -->
    <div class="invoice-modal" v-if="invoiceDialogVisible">
      <div class="invoice-modal-content">
        <div class="invoice-modal-header">
          <h3 class="invoice-modal-title">开具发票</h3>
          <span class="invoice-modal-close" @click="closeInvoiceDialog">×</span>
        </div>
        <div class="invoice-modal-body" v-if="currentInvoiceRow">
          <div class="form-item">
            <label class="form-label">订单号:</label>
            <div class="form-value">{{ currentInvoiceRow.orderNo }}</div>
          </div>
          <div class="form-item">
            <label class="form-label">买家姓名:</label>
            <div class="form-value">{{ currentInvoiceRow.userName }}</div>
          </div>
          <div class="form-item">
            <label class="form-label">接收邮箱:</label>
            <div class="form-value">{{ currentInvoiceRow.invoiceEmail }}</div>
          </div>
          <div class="form-item">
            <label class="form-label">发票PDF:</label>
            <div class="upload-area">
              <input type="file" ref="pdfFileInput" class="file-input" accept=".pdf" @change="handlePdfUpload">
              <button class="upload-btn" @click="triggerPdfInput">点击上传</button>
              <div class="upload-tip">只能上传PDF文件，且不超过10MB</div>
              <div class="upload-file-name" v-if="invoiceForm.invoiceFileName">
                {{ invoiceForm.invoiceFileName }}
                <span class="file-remove" @click="removePdfFile">×</span>
              </div>
              <div class="error-text" v-if="showPdfError">请上传发票PDF文件</div>
            </div>
          </div>
          <div class="form-item">
            <label class="form-label">发送截图:</label>
            <div class="upload-area">
              <input type="file" ref="imageFileInput" class="file-input" accept=".jpg,.jpeg,.png,.gif"
                @change="handleImageUpload">
              <button class="upload-btn" @click="triggerImageInput">点击上传</button>
              <div class="upload-tip">只能上传jpg/png/gif文件，且不超过5MB</div>
              <div class="upload-preview" v-if="invoiceForm.imagePreviewUrl">
                <img :src="invoiceForm.imagePreviewUrl" class="preview-img">
                <span class="preview-remove" @click="removeImageFile">×</span>
              </div>
              <div class="error-text" v-if="showImageError">请上传发送邮箱截图</div>
            </div>
          </div>
        </div>
        <div class="invoice-modal-footer">
          <button class="modal-btn cancel-btn" @click="closeInvoiceDialog">取消</button>
          <button class="modal-btn confirm-btn" @click="confirmInvoice"
            :disabled="!invoiceForm.invoiceFileUrl || !invoiceForm.emailScreenshotUrl || loading">
            {{ loading ? '提交中...' : '确认开票' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 查看开票记录弹窗 -->
    <div class="view-modal" v-if="viewRecordDialogVisible">
      <div class="view-modal-content">
        <div class="view-modal-header">
          <h3 class="view-modal-title">开票记录详情</h3>
          <span class="view-modal-close" @click="closeViewDialog">×</span>
        </div>
        <div class="view-modal-body" v-if="currentRecordRow">
          <div class="record-item">
            <span class="record-label">订单号：</span>
            <span class="record-value">{{ currentRecordRow.orderNo }}</span>
          </div>
          <div class="record-item">
            <span class="record-label">买家ID：</span>
            <span class="record-value">{{ currentRecordRow.userId || '-' }}</span>
          </div>
          <div class="record-item">
            <span class="record-label">买家姓名：</span>
            <span class="record-value">{{ currentRecordRow.userName }}</span>
          </div>
          <div class="record-item">
            <span class="record-label">开票金额：</span>
            <span class="record-value">¥{{ currentRecordRow.invoiceAmount }}</span>
          </div>
          <div class="record-item">
            <span class="record-label">发票类型：</span>
            <span class="record-value">{{ currentRecordRow.invoiceType }}</span>
          </div>
          <div class="record-item">
            <span class="record-label">开票类型：</span>
            <span class="record-value">{{ currentRecordRow.invoicePersonType }}</span>
          </div>
          <div class="record-item">
            <span class="record-label">发票抬头：</span>
            <span class="record-value">{{ currentRecordRow.invoiceTitle }}</span>
          </div>
          <div class="record-item">
            <span class="record-label">纳税人识别号：</span>
            <span class="record-value">{{ currentRecordRow.taxpayerId || '-' }}</span>
          </div>
          <div class="record-item">
            <span class="record-label">联系方式：</span>
            <span class="record-value">{{ currentRecordRow.contact || '-' }}</span>
          </div>
          <div class="record-item">
            <span class="record-label">接收邮箱：</span>
            <span class="record-value">{{ currentRecordRow.invoiceEmail }}</span>
          </div>
          <div class="record-item">
            <span class="record-label">申请时间：</span>
            <span class="record-value">{{ currentRecordRow.applyTime }}</span>
          </div>
          <div class="record-item">
            <span class="record-label">完成时间：</span>
            <span class="record-value">{{ currentRecordRow.completeTime || '-' }}</span>
          </div>
          <div class="record-item">
            <span class="record-label">发票文件：</span>
            <span class="record-value">
              <a :href="currentRecordRow.invoiceFileUrl" target="_blank" class="file-link"
                v-if="currentRecordRow.invoiceFileUrl">
                {{ currentRecordRow.pdfName || '查看发票PDF' }}
              </a>
              <span v-else>-</span>
            </span>
          </div>
          <div class="record-item">
            <span class="record-label">发送截图：</span>
            <span class="record-value">
              <img :src="currentRecordRow.emailScreenshotUrl" class="screenshot-img"
                v-if="currentRecordRow.emailScreenshotUrl" @click="previewImage(currentRecordRow.emailScreenshotUrl)">
              <span v-else>-</span>
            </span>
          </div>
        </div>
        <div class="view-modal-footer">
          <button class="modal-btn close-btn" @click="closeViewDialog">关闭</button>
        </div>
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <div class="image-preview-modal" v-if="imagePreviewVisible">
      <div class="preview-overlay" @click="closeImagePreview"></div>
      <img :src="previewImageUrl" class="preview-full-img">
    </div>
  </div>
</template>

<script>
export default {
  name: 'DesignerInvoicePage',
  data() {
    return {
      // 实名认证相关
      isAuthenticated: null,
      showAuthModal: null,
      // 筛选表单
      filterForm: {
        keyword: '',
        invoiceStatus: '',
        invoicePersonType: ''
      },
      // 列表数据
      invoiceList: [],
      loading: false,
      total: 0,
      // 分页参数
      pagination: {
        pageNum: 1,
        pageSize: 10
      },
      // 开票弹窗
      invoiceDialogVisible: false,
      currentInvoiceRow: null,
      showPdfError: false,
      showImageError: false,
      invoiceForm: {
        invoiceFileList: [],
        emailScreenshotList: [],
        invoiceFileName: '',
        invoiceFileUrl: '',
        emailScreenshotUrl: '',
        imagePreviewUrl: ''
      },
      // 查看记录弹窗
      viewRecordDialogVisible: false,
      currentRecordRow: null,
      // 图片预览
      imagePreviewVisible: false,
      previewImageUrl: ''
    }
  },
  async mounted() {
    // 检查实名认证状态
    await this.checkAuthStatus();
    // 已认证则加载数据
    if (this.isAuthenticated === true) {
      this.getInvoiceList();
    }
  },
  methods: {
    // 检查实名认证状态
    async checkAuthStatus() {
      try {
        const res = await this.$apiFun.getSeleMerchantCA();
        if (res.status === 200 || res.code === 200) {
          this.isAuthenticated = !!res.obj?.merchantCA;
          this.showAuthModal = !this.isAuthenticated;
        } else {
          this.$message?.error("认证状态查询失败");
          this.isAuthenticated = false;
          this.showAuthModal = true;
        }
      } catch (err) {
        this.$message?.error('认证状态查询失败');
        console.error('查询认证状态出错：', err);
        this.isAuthenticated = false;
        this.showAuthModal = true;
      }
    },
    // 跳转到实名认证页面
    goToAuthPage() {
      this.showAuthModal = false;
      this.$router?.push('/dashboard/seller/info');
    },
    // 获取开票列表数据
    async getInvoiceList() {
      // 未认证拦截
      if (!this.isAuthenticated) {
        this.$message?.warning('请先完成实名认证');
        this.showAuthModal = true;
        return;
      }

      this.loading = true;
      try {
        const params = {
          type: 0,
          title: this.filterForm.keyword.trim(),
          invoiceStatus: this.filterForm.invoiceStatus,
          invoicePersonType: this.filterForm.invoicePersonType,
          pageIndex: this.pagination.pageNum,
          pageSize: this.pagination.pageSize,
        };

        // 调用设计师开票列表接口（替换为实际接口）
        const res = await this.$apiFun.getInvoiceDetails(params);

        if (res.status === 200 || res.code === 200) {
          this.invoiceList = res.obj || [];
          this.total = res.num || 0;
        } else {
          this.$message?.warning(`获取开票列表失败：${res.msg || '请求失败'}`);
          this.invoiceList = [];
          this.total = 0;
        }
      } catch (error) {
        console.error('获取开票列表失败：', error);
        this.$message?.error('获取开票列表失败，请稍后重试');
        this.invoiceList = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    // 搜索
    handleSearch() {
      this.pagination.pageNum = 1;
      this.getInvoiceList();
    },
    // 重置
    handleReset() {
      this.filterForm = {
        keyword: '',
        invoiceStatus: '',
        invoicePersonType: ''
      };
      this.pagination.pageNum = 1;
      this.getInvoiceList();
    },
    // 分页切换
    handleCurrentChange(val) {
      this.pagination.pageNum = val;
      this.getInvoiceList();
    },
    // 打开开票弹窗
    openInvoiceDialog(row) {
      // 未认证拦截
      if (!this.isAuthenticated) {
        this.$message?.warning('请先完成实名认证');
        this.showAuthModal = true;
        return;
      }

      this.currentInvoiceRow = JSON.parse(JSON.stringify(row));
      // 重置表单状态
      this.showPdfError = false;
      this.showImageError = false;
      this.invoiceForm = {
        invoiceFileList: [],
        emailScreenshotList: [],
        invoiceFileName: '',
        invoiceFileUrl: '',
        emailScreenshotUrl: '',
        imagePreviewUrl: ''
      };
      this.invoiceDialogVisible = true;
    },
    // 关闭开票弹窗
    closeInvoiceDialog() {
      this.invoiceDialogVisible = false;
      this.currentInvoiceRow = null;
    },
    // 触发PDF文件选择
    triggerPdfInput() {
      this.$refs.pdfFileInput.click();
    },
    // 处理PDF上传
    async handlePdfUpload(e) {
      const file = e.target.files[0];
      if (!file) return;

      // 文件类型校验
      const isPDF = file.type === 'application/pdf';
      const isLt10M = file.size / 1024 / 1024 < 10;

      if (!isPDF) {
        this.$message?.error('只能上传PDF格式的文件!');
        return;
      }
      if (!isLt10M) {
        this.$message?.error('文件大小不能超过10MB!');
        return;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', 'invoice_pdf');
        formData.append('orderNo', this.currentInvoiceRow.orderNo);

        // 调用文件上传接口
        const res = await this.$apiFun.postUploadInvoice(formData);
        if (res.status === 200 && res.obj) {
          this.invoiceForm.invoiceFileName = res.obj.fileName || file.name;
          this.invoiceForm.invoiceFileUrl = res.obj.fileUrl || '';
          this.$message?.success('发票文件上传成功');
          this.showPdfError = false;
        } else {
          this.$message?.error(`发票PDF上传失败：${res.msg || '上传失败'}`);
          this.showPdfError = true;
        }
      } catch (error) {
        console.error('发票PDF上传失败：', error);
        this.$message?.error('发票PDF上传失败，请稍后重试');
        this.showPdfError = true;
      }

      // 清空input值，允许重复选择同一文件
      e.target.value = '';
    },
    // 移除PDF文件
    removePdfFile() {
      this.invoiceForm.invoiceFileName = '';
      this.invoiceForm.invoiceFileUrl = '';
      this.showPdfError = false;
    },
    // 触发图片选择
    triggerImageInput() {
      this.$refs.imageFileInput.click();
    },
    // 处理图片上传
    async handleImageUpload(e) {
      const file = e.target.files[0];
      if (!file) return;

      // 文件类型校验
      const isImage = ['image/jpg', 'image/jpeg', 'image/png', 'image/gif'].includes(file.type);
      const isLt5M = file.size / 1024 / 1024 < 5;

      if (!isImage) {
        this.$message?.error('只能上传jpg/png/gif格式的图片!');
        return;
      }
      if (!isLt5M) {
        this.$message?.error('图片大小不能超过5MB!');
        return;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', 'email_screenshot');
        formData.append('orderNo', this.currentInvoiceRow.orderNo);

        // 调用文件上传接口
        const res = await this.$apiFun.postUploadInvoice(formData);
        if (res.status === 200 && res.obj) {
          this.invoiceForm.emailScreenshotUrl = res.obj.fileUrl || '';
          // 生成预览图
          this.invoiceForm.imagePreviewUrl = URL.createObjectURL(file);
          this.$message?.success('截图上传成功');
          this.showImageError = false;
        } else {
          this.$message?.error(`发送截图上传失败：${res.msg || '上传失败'}`);
          this.showImageError = true;
        }
      } catch (error) {
        console.error('发送截图上传失败：', error);
        this.$message?.error('发送截图上传失败，请稍后重试');
        this.showImageError = true;
      }

      // 清空input值
      e.target.value = '';
    },
    // 移除图片文件
    removeImageFile() {
      this.invoiceForm.emailScreenshotUrl = '';
      this.invoiceForm.imagePreviewUrl = '';
      this.showImageError = false;
    },
    // 确认开票
    async confirmInvoice() {
      // 手动校验
      let hasError = false;
      if (!this.invoiceForm.invoiceFileUrl) {
        this.showPdfError = true;
        hasError = true;
      }
      if (!this.invoiceForm.emailScreenshotUrl) {
        this.showImageError = true;
        hasError = true;
      }
      if (hasError) {
        this.$message?.warning('请完成所有文件上传后再提交');
        return;
      }

      this.loading = true;
      try {
        // 构造提交参数
        const submitData = {
          PDFName: this.invoiceForm.invoiceFileName,
          PDFUrl: this.invoiceForm.invoiceFileUrl,
          ScreenshotUrl: this.invoiceForm.emailScreenshotUrl,
          id: this.currentInvoiceRow.id
        };

        // 调用开票提交接口
        const res = await this.$apiFun.postSubmission(submitData);
        if (res.status === 200 || res.code === 200) {
          this.$message?.success('开票信息提交成功');
          this.invoiceDialogVisible = false;
          // 重新加载列表
          this.getInvoiceList();
        } else {
          this.$message?.error(`开票信息提交失败：${res.msg || '请求失败'}`);
        }
      } catch (error) {
        console.error('确认提交开票信息失败：', error);
        this.$message?.error('开票信息提交失败，请稍后重试');
      } finally {
        this.loading = false;
      }
    },
    // 查看开票记录
    viewInvoiceRecord(row) {
      this.currentRecordRow = JSON.parse(JSON.stringify(row));
      this.viewRecordDialogVisible = true;
    },
    // 关闭查看弹窗
    closeViewDialog() {
      this.viewRecordDialogVisible = false;
      this.currentRecordRow = null;
    },
    // 预览图片
    previewImage(url) {
      this.previewImageUrl = url;
      this.imagePreviewVisible = true;
    },
    // 关闭图片预览
    closeImagePreview() {
      this.imagePreviewVisible = false;
      this.previewImageUrl = '';
    }
  }
}
</script>

<style lang="less" scoped>
// 沿用模型管理页的深色变量
@bg-primary: transparent;
@bg-secondary: rgba(0,0,0,0.2);
@text-primary: #e0e0e0;
@text-secondary: #a0a0a0;
@accent-blue: #0ea5e9;
@border-color: #444;
@input-bg: #333;
@hover-bg: #333333;
@success-color: #10b981;
@warning-color: #f59e0b;
@error-color: #ef4444;

.invoice-page {
  color: @text-primary;
  min-height: 100vh;
  padding: 20px;
  box-sizing: border-box;
  position: relative;
  background-color: @bg-primary;

  // 实名认证弹窗（复用模型管理页样式）
  .auth-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;

    .auth-modal-content {
      background-color: @bg-secondary;
      border-radius: 8px;
      width: 400px;
      padding: 30px 20px;
      text-align: center;

      .auth-modal-title {
        font-size: 20px;
        color: @text-primary;
        margin-bottom: 15px;
        font-weight: 500;
      }

      .auth-modal-desc {
        font-size: 14px;
        color: @text-secondary;
        line-height: 1.8;
        margin-bottom: 20px;
      }

      .auth-modal-btn {
        background-color: @accent-blue;
        color: #fff;
        border: none;
        padding: 10px 20px;
        border-radius: 4px;
        font-size: 16px;
        cursor: pointer;
        width: 100%;
        max-width: 200px;
      }
    }
  }

  // 未认证提示（复用模型管理页样式）
  .unauth-tip {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    background-color: @bg-secondary;
    border-radius: 6px;
    padding: 20px;
    margin-top: 20px;

    .tip-content {
      text-align: center;

      .tip-icon {
        display: inline-block;
        width: 60px;
        height: 60px;
        background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024"><path fill="%230ea5e9" d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z"/><path fill="%230ea5e9" d="M512 140c-205.4 0-372 166.6-372 372s166.6 372 372 372 372-125.4 372-372-125.4-280-280-280zm0 508c-125.9 0-228-102.1-228-228s102.1-228 228-228 228 102.1 228 228-102.1 228-228 228z"/></svg>') no-repeat center;
        margin-bottom: 20px;
      }

      p {
        font-size: 16px;
        color: @text-secondary;
        margin-bottom: 20px;
      }

      .tip-btn {
        background-color: @accent-blue;
        color: #fff;
        border: none;
        padding: 10px 20px;
        border-radius: 4px;
        font-size: 14px;
        cursor: pointer;
      }
    }
  }

  // 筛选区域
  .filter-area {
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 15px;
    flex-wrap: wrap;
    background-color: @bg-secondary;
    padding: 15px;
    border-radius: 6px;

    .filter-item {
      display: flex;
      align-items: center;
      gap: 8px;

      .filter-label {
        font-size: 14px;
        color: @text-secondary;
        min-width: 60px;
      }

      .filter-input,
      .filter-select {
        background-color: @input-bg;
        border: 1px solid @border-color;
        color: @text-primary;
        padding: 6px 10px;
        border-radius: 4px;
        font-size: 13px;
        min-width: 180px;

        &::placeholder {
          color: @text-secondary;
        }

        &:focus {
          outline: none;
          border-color: @accent-blue;
        }
      }

      .filter-select {
        min-width: 140px;
      }
    }

    .filter-buttons {
      display: flex;
      gap: 8px;

      .filter-btn {
        background-color: @bg-secondary;
        border: 1px solid @border-color;
        color: @text-primary;
        padding: 6px 12px;
        border-radius: 4px;
        font-size: 13px;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
          border-color: @accent-blue;
          color: @accent-blue;
        }
      }
    }
  }

  // 开票列表
  .invoice-list {
    background-color: @bg-secondary;
    border-radius: 6px;
    overflow: hidden;

    .invoice-table {
      width: 100%;
      border-collapse: collapse;

      th {
        background-color: #333;
        color: @text-primary;
        font-weight: 500;
        padding: 12px 10px;
        text-align: left;
        border-bottom: 1px solid @border-color;
        font-size: 14px;
      }

      td {
        padding: 12px 10px;
        border-bottom: 1px solid @border-color;
        font-size: 13px;
        color: @text-primary;
        vertical-align: middle;
      }

      tr:hover {
        background-color: @hover-bg;
      }

      // 买家信息
      .buyer-info {
        display: flex;
        align-items: center;
        gap: 8px;

        .buyer-avatar {
          width: 40px;
          height: 40px;
          border-radius: 10%;
          object-fit: cover;
        }

        .buyer-detail {
          .buyer-name {
            color: @text-primary;
            margin-bottom: 2px;
          }

          .buyer-contact {
            color: @text-secondary;
            font-size: 12px;
          }
        }
      }

      // 价格样式
      .price {
        color: @accent-blue;
        font-weight: 500;
      }

      // 发票抬头
      .title-text {
        max-width: 200px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      // 标签样式
      .tag {
        padding: 2px 8px;
        border-radius: 4px;
        font-size: 12px;
        display: inline-block;

        &.info-tag {
          background-color: rgba(14, 165, 233, 0.2);
          color: @accent-blue;
        }

        &.success-tag {
          background-color: rgba(16, 185, 129, 0.2);
          color: @success-color;
        }

        &.warning-tag {
          background-color: rgba(245, 158, 11, 0.2);
          color: @warning-color;
        }
      }

      // 操作按钮
      .operate-btn {
        padding: 4px 10px;
        border-radius: 4px;
        font-size: 12px;
        cursor: pointer;
        transition: all 0.2s;
        border: 1px solid @border-color;
        margin-right: 5px;

        &.primary-btn {
          background-color: @accent-blue;
          color: #fff;
          border-color: @accent-blue;

          &:hover {
            background-color: #0284c7;
            border-color: #0284c7;
          }
        }

        &.text-btn {
          background-color: transparent;
          color: @accent-blue;
          border-color: transparent;

          &:hover {
            color: #38bdf8;
            text-decoration: underline;
          }
        }
      }
    }
  }

  // 分页样式（复用模型管理页）
  .pagination-container {
    margin-top: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px 0;

    :deep(.el-pagination) {
      background-color: transparent !important;

      .el-pagination__total {
        color: #e0e0e0 !important;
        font-size: 14px !important;
      }

      .el-pager li {
        background-color: #080a12 !important;
        color: #a0a0a0 !important;
        border: 1px solid #424242 !important;
        margin: 0 2px !important;

        &:hover {
          background-color: rgba(255, 255, 255, 0.1) !important;
          color: #0ea5e9 !important;
          border-color: #0ea5e9 !important;
        }

        &.active {
          background-color: #0ea5e9 !important;
          color: #ffffff !important;
          border-color: #0ea5e9 !important;
        }
      }

      .btn-prev,
      .btn-next {
        background-color: @bg-secondary !important;
        color: @text-secondary !important;
        border: 1px solid @border-color !important;
        min-width: 30px !important;
        margin: 0 2px !important;

        &.disabled {
          background-color: darken(@bg-secondary, 5%) !important;
          color: darken(@text-secondary, 20%) !important;
          border-color: darken(@border-color, 10%) !important;
          cursor: not-allowed !important;
        }

        &:not(.disabled):hover {
          background-color: @hover-bg !important;
          color: @accent-blue !important;
          border-color: @accent-blue !important;
        }
      }

      .el-pagination__jump {
        color: #a0a0a0 !important;

        .el-input__inner {
          background-color: #080a12 !important;
          color: #e0e0e0 !important;
          border: 1px solid #424242 !important;

          &:focus {
            border-color: #0ea5e9 !important;
          }
        }
      }
    }
  }

  // 开票弹窗
  .invoice-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1001;

    .invoice-modal-content {
      background-color: #1e1e1e;
      border-radius: 8px;
      width: 600px;
      max-width: 90%;
      padding: 0;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);

      .invoice-modal-header {
        padding: 15px 20px;
        border-bottom: 1px solid @border-color;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .invoice-modal-title {
          font-size: 18px;
          color: @text-primary;
          margin: 0;
          font-weight: 500;
        }

        .invoice-modal-close {
          font-size: 24px;
          color: @text-secondary;
          cursor: pointer;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10%;
          transition: all 0.2s;

          &:hover {
            background-color: @hover-bg;
            color: @accent-blue;
          }
        }
      }

      .invoice-modal-body {
        padding: 20px;

        .form-item {
          margin-bottom: 15px;

          .form-label {
            display: block;
            font-size: 14px;
            color: @text-primary;
            margin-bottom: 8px;
            font-weight: 500;
          }

          .form-value {
            color: @text-secondary;
            font-size: 13px;
            background-color: @input-bg;
            padding: 8px;
            border-radius: 4px;
          }

          .upload-area {
            position: relative;

            .file-input {
              display: none;
            }

            .upload-btn {
              background-color: @accent-blue;
              color: #fff;
              border: none;
              padding: 8px 15px;
              border-radius: 4px;
              font-size: 13px;
              cursor: pointer;
              margin-bottom: 8px;
            }

            .upload-tip {
              font-size: 12px;
              color: @text-secondary;
              margin-bottom: 8px;
            }

            .upload-file-name {
              color: @accent-blue;
              font-size: 13px;
              display: flex;
              align-items: center;
              gap: 5px;
              margin-top: 8px;

              .file-remove {
                color: @error-color;
                cursor: pointer;
                font-size: 16px;
              }
            }

            .upload-preview {
              margin-top: 8px;
              position: relative;

              .preview-img {
                width: 100px;
                height: 80px;
                object-fit: cover;
                border-radius: 4px;
              }

              .preview-remove {
                position: absolute;
                top: -5px;
                right: -5px;
                background-color: @error-color;
                color: #fff;
                width: 18px;
                height: 18px;
                border-radius: 10%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 12px;
                cursor: pointer;
              }
            }

            .error-text {
              color: @error-color;
              font-size: 12px;
              margin-top: 4px;
            }
          }
        }
      }

      .invoice-modal-footer {
        padding: 15px 20px;
        border-top: 1px solid @border-color;
        display: flex;
        justify-content: flex-end;
        gap: 10px;

        .modal-btn {
          padding: 8px 20px;
          border-radius: 4px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;

          &.cancel-btn {
            background-color: @bg-secondary;
            color: @text-primary;
            border: 1px solid @border-color;

            &:hover {
              border-color: @accent-blue;
              color: @accent-blue;
            }
          }

          &.confirm-btn {
            background-color: @accent-blue;
            color: #fff;
            border: 1px solid @accent-blue;

            &:hover:not(:disabled) {
              background-color: #0284c7;
              border-color: #0284c7;
            }

            &:disabled {
              background-color: #4a4a4a;
              border-color: #4a4a4a;
              color: #999;
              cursor: not-allowed;
              opacity: 0.8;
            }
          }
        }
      }
    }
  }

  // 查看记录弹窗
  .view-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1001;

    .view-modal-content {
      background-color: @bg-secondary;
      border-radius: 8px;
      width: 700px;
      max-width: 90%;
      max-height: 90vh;
      display: flex;
      flex-direction: column;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);

      .view-modal-header {
        padding: 15px 20px;
        border-bottom: 1px solid @border-color;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .view-modal-title {
          font-size: 18px;
          color: @text-primary;
          margin: 0;
          font-weight: 500;
        }

        .view-modal-close {
          font-size: 24px;
          color: @text-secondary;
          cursor: pointer;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10%;
          transition: all 0.2s;

          &:hover {
            background-color: @hover-bg;
            color: @accent-blue;
          }
        }
      }

      .view-modal-body {
        padding: 20px;
        overflow-y: auto;
        flex: 1;

        .record-item {
          display: flex;
          margin-bottom: 12px;
          align-items: flex-start;

          .record-label {
            width: 100px;
            color: @text-secondary;
            font-weight: 500;
            flex-shrink: 0;
            font-size: 13px;
          }

          .record-value {
            flex: 1;
            color: @text-primary;
            word-break: break-all;
            font-size: 13px;

            .file-link {
              color: @accent-blue;
              text-decoration: underline;

              &:hover {
                color: #38bdf8;
              }
            }

            .screenshot-img {
              width: 100px;
              height: 80px;
              object-fit: cover;
              border-radius: 4px;
              cursor: pointer;
            }
          }
        }
      }

      .view-modal-footer {
        padding: 15px 20px;
        border-top: 1px solid @border-color;
        display: flex;
        justify-content: center;

        .modal-btn {
          padding: 8px 30px;
          border-radius: 4px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;

          &.close-btn {
            background-color: @bg-secondary;
            color: @text-primary;
            border: 1px solid @border-color;

            &:hover {
              border-color: @accent-blue;
              color: @accent-blue;
            }
          }
        }
      }
    }
  }

  // 图片预览弹窗
  .image-preview-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1002;
    display: flex;
    align-items: center;
    justify-content: center;

    .preview-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.9);
      cursor: pointer;
    }

    .preview-full-img {
      max-width: 90%;
      max-height: 90%;
      z-index: 1;
      border-radius: 4px;
    }
  }

  // 响应式适配
  @media (max-width: 768px) {
    padding: 10px;

    .filter-area {
      flex-direction: column;
      align-items: stretch;

      .filter-item {
        width: 100%;
      }

      .filter-buttons {
        width: 100%;
        justify-content: flex-end;
      }
    }

    .invoice-table {

      th,
      td {
        padding: 8px 5px;
        font-size: 12px;
      }

      .title-text {
        max-width: 100px;
      }
    }
  }
}
</style>